import { useState, useEffect, useMemo, memo } from "react";
import { getAvatarColor, getName } from "@/shared/utils/helperFunctions";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs) => twMerge(clsx(inputs));

const CommonAvatar = ({
  imageUrl = "",
  name = "",
  className = "",
  textClassName = "",
  size = "md",
  loading = "lazy",
}) => {
  const [error, setError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Memoize initials
  const initials = useMemo(() => getName(name || ""), [name]);

  // Memoize avatar color
  const avatarColorClass = useMemo(() => {
    const identifier = name || imageUrl || "default";
    return getAvatarColor(identifier);
  }, [name, imageUrl]);

  // Size variants
  const sizeClasses = {
    sm: "w-6 h-6 text-[10px]",
    md: "w-8 h-8 text-xs",
    lg: "w-10 h-10 text-sm",
    xl: "w-12 h-12 text-base",
  };

  // Reset states when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      setError(false);
      setImageLoaded(false);
    } else {
      setError(false);
      setImageLoaded(false);
    }
  }, [imageUrl]);

  const showFallback = !imageUrl || error;
  const showImage = imageUrl && !error && imageLoaded;
  // Show colored background
  const isShowingInitials = showFallback || (imageUrl && !imageLoaded);

  // Determine background color
  const backgroundColorClass = isShowingInitials
    ? avatarColorClass
    : `${showImage ? "bg-transparent" : " bg-gray-200"}`;

  return (
    <div
      className={cn(
        "shrink-0 rounded-full flex items-center justify-center overflow-hidden relative border border-gray-100",
        sizeClasses[size],
        backgroundColorClass,
        className,
      )}
      role="img"
      aria-label={name ? `Avatar for ${name}` : "User avatar"}
    >
      {/* Show fallback initials */}
      {(showFallback || !imageLoaded) && initials && (
        <span
          className={cn(
            "font-semibold select-none",
            showImage
              ? "opacity-0"
              : "opacity-100 transition-opacity duration-200",
            textClassName,
          )}
        >
          {initials}
        </span>
      )}

      {/* Show image when loaded */}
      {imageUrl && !error && (
        <img
          src={imageUrl}
          alt={name ? `${name}'s avatar` : "Avatar"}
          className={cn(
            "w-full h-full object-cover absolute inset-0 transition-opacity duration-200",
            imageLoaded ? "opacity-100" : "opacity-0",
          )}
          loading={loading}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setError(true);
            setImageLoaded(false);
          }}
          draggable={false}
        />
      )}
    </div>
  );
};

export default memo(CommonAvatar);
